"use client";
import React, { useEffect, useRef, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Text } from "@/components/ui/typography";
import {
  Sparkles, Loader2, CheckCircle2, AlertTriangle, ChevronDown, ChevronUp,
} from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * AI review panel — DISPLAY ONLY.
 *
 * It no longer uploads the video (that was the desktop "double upload"). The
 * save endpoint (/api/upload-video) now generates the review in the background,
 * and this panel POLLS /api/video-resume/analysis for the result. So the video
 * is uploaded exactly once.
 *
 * Props:
 *   active             — render + start polling when true
 *   justSaved          — a fresh save just happened → show "Reviewing…" and wait
 *                        for a review NEWER than baselineAnalyzedAt
 *   baselineAnalyzedAt — analyzed_at that existed before this save (freshness marker)
 */
function VideoAnalysisFeedback({ active = false, justSaved = false, baselineAnalyzedAt = null }) {
  const [analysis, setAnalysis] = useState(null);
  const [status, setStatus] = useState("idle"); // idle | reviewing | ready | none
  const [showTranscript, setShowTranscript] = useState(false);
  const pollRef = useRef({ active: false });

  useEffect(() => {
    if (!active) return undefined;
    pollRef.current.active = true;
    let tries = 0;
    const maxTries = 24; // ~60s at 2.5s
    setStatus(justSaved ? "reviewing" : "idle");

    const isFresh = (a) => !!(a && a.analyzed_at && (!justSaved || a.analyzed_at !== baselineAnalyzedAt));

    const tick = async () => {
      if (!pollRef.current.active) return;
      tries += 1;
      try {
        const resp = await fetch("/api/video-resume/analysis", { credentials: "include" });
        const json = await resp.json();
        const a = json && json.analysis;
        if (isFresh(a)) {
          if (!pollRef.current.active) return;
          setAnalysis(a);
          setStatus("ready");
          return;
        }
        if (!justSaved) {
          // Revisiting an existing video — show whatever review exists, no spinner.
          if (a) { setAnalysis(a); setStatus("ready"); } else { setStatus("none"); }
          return; // one-shot
        }
      } catch { /* transient — keep polling */ }
      if (tries >= maxTries) {
        if (!pollRef.current.active) return;
        setStatus(analysis ? "ready" : "none");
        return;
      }
      setTimeout(tick, 2500);
    };
    tick();
    return () => { pollRef.current.active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active, justSaved, baselineAnalyzedAt]);

  if (!active) return null;

  const ready = analysis?.verdict === "ready";
  const metrics = analysis?.metrics || {};

  if (status === "reviewing" && !analysis) {
    return (
      <Card className={cn("w-full border-primary/30")}>
        <CardContent className={cn("p-4")}>
          <div className={cn("flex items-center gap-2 text-primary font-medium")}>
            <Sparkles className="w-4 h-4" />
            <span className={cn("text-sm")}>Reviewing your video…</span>
            <Loader2 className="w-4 h-4 animate-spin" />
          </div>
          <div className={cn("h-1.5 w-full rounded-full bg-muted overflow-hidden mt-3")}>
            <div className={cn("h-full w-1/3 bg-primary/70 rounded-full animate-pulse")} />
          </div>
          <Text variant="muted" className={cn("text-xs mt-2")}>
            This takes up to a minute — your video is already saved.
          </Text>
        </CardContent>
      </Card>
    );
  }

  if (status === "none" && !analysis) {
    return (
      <Card className={cn("w-full border-border")}>
        <CardContent className={cn("p-4")}>
          <Text variant="muted" className={cn("text-sm")}>
            Your video is saved. An AI review will appear here shortly — refresh
            this page in a minute if you don&apos;t see it.
          </Text>
        </CardContent>
      </Card>
    );
  }

  if (!analysis) return null;

  return (
    <Card className={cn("w-full border-primary/30")}>
      <CardContent className={cn("p-4")}>
        <div className={cn("space-y-4")}>
          {/* Verdict header */}
          <div className={cn("flex items-center justify-between")}>
            <div className={cn("flex items-center gap-2")}>
              {ready ? (
                <CheckCircle2 className="w-5 h-5 text-success" />
              ) : (
                <AlertTriangle className="w-5 h-5 text-warning" />
              )}
              <Text className={cn("font-semibold text-foreground")}>
                {ready ? "Looking good" : "Worth one more take"}
              </Text>
            </div>
            <Badge variant={ready ? "default" : "secondary"} className={cn("text-sm font-mono")}>
              {analysis.score}/100
            </Badge>
          </div>

          {/* Honest metrics row */}
          <div className={cn("flex flex-wrap gap-2")}>
            {metrics.duration_seconds != null && (
              <Badge variant="outline" className={cn("text-xs")}>{Math.round(metrics.duration_seconds)}s long</Badge>
            )}
            {metrics.wpm != null && (
              <Badge variant="outline" className={cn("text-xs")}>{Math.round(metrics.wpm)} words/min</Badge>
            )}
            {metrics.filler_count != null && (
              <Badge variant="outline" className={cn("text-xs")}>{metrics.filler_count} filler words</Badge>
            )}
          </div>

          {/* Strengths */}
          {analysis.strengths?.length > 0 && (
            <div>
              <Text variant="small" className={cn("font-semibold text-success mb-1")}>What worked</Text>
              <ul className={cn("space-y-1")}>
                {analysis.strengths.map((s, i) => (
                  <li key={i} className={cn("text-sm text-muted-foreground flex gap-2")}>
                    <span className={cn("text-success")}>+</span>{s}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Improvements */}
          {analysis.improvements?.length > 0 && (
            <div>
              <Text variant="small" className={cn("font-semibold text-warning mb-1")}>What to improve</Text>
              <ul className={cn("space-y-1")}>
                {analysis.improvements.map((s, i) => (
                  <li key={i} className={cn("text-sm text-muted-foreground flex gap-2")}>
                    <span className={cn("text-warning")}>→</span>{s}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Transcript toggle */}
          {analysis.transcript && (
            <div>
              <button
                type="button"
                onClick={() => setShowTranscript(v => !v)}
                className={cn("flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors")}
              >
                {showTranscript ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                {showTranscript ? "Hide transcript" : "Show what we heard"}
              </button>
              {showTranscript && (
                <Text variant="muted" className={cn("text-xs mt-2 max-h-32 overflow-y-auto rounded bg-muted/50 p-3 leading-relaxed")}>
                  {analysis.transcript}
                </Text>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export default VideoAnalysisFeedback;
